/**
 * The topology compositor.
 *
 * The one rule this file exists to enforce: a set of affected cells is not a set of
 * effects. Thirteen cells of a burst are ONE explosion, and an L-shaped cluster is
 * one field with one outline, not seven squares that happen to touch. So the cells
 * are merged into connected components first, and everything downstream — surface,
 * contour, noise space, particle domain, volume — is built per component.
 *
 * Everything here works in GRID SPACE: the unit is one cell, cell (col,row) covers
 * [col, col+1] x [row, row+1], and x runs east while z runs south. Nothing here
 * knows about pixels, zoom, pan or the board's corner morph — that is the
 * projection's job (see projection.js), and keeping it out means the topology is
 * identical at any zoom and can be cached by cells alone.
 *
 * No three.js here on purpose: this is the part that has to be unit-testable.
 */

export const SDF_RANGE = 3.0;

export const cellKey = (col, row) => `${col},${row}`;

/** Distinct, integral, finite cells, in a stable order. */
export function normalizeCells(cells) {
  const seen = new Set();
  const out = [];
  (cells || []).forEach(cell => {
    if (!cell) return;
    const col = Math.round(cell.col ?? cell.x);
    const row = Math.round(cell.row ?? cell.y);
    if (!Number.isFinite(col) || !Number.isFinite(row)) return;
    const key = cellKey(col, row);
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ col, row });
  });
  out.sort((a, b) => (a.row - b.row) || (a.col - b.col));
  return out;
}

/**
 * Four-directional components. Diagonal touching is NOT connection: two cells that
 * meet at a corner share no edge, so an effect drawn across them would have to leap
 * a gap. They become two components and therefore two effects, which is what the
 * spec asks for and what the ground actually looks like.
 */
export function connectedComponents(cells) {
  const list = normalizeCells(cells);
  const present = new Set(list.map(c => cellKey(c.col, c.row)));
  const visited = new Set();
  const components = [];

  list.forEach(start => {
    const startKey = cellKey(start.col, start.row);
    if (visited.has(startKey)) return;
    visited.add(startKey);
    const component = [];
    const queue = [start];
    while (queue.length) {
      const cell = queue.pop();
      component.push(cell);
      [[1, 0], [-1, 0], [0, 1], [0, -1]].forEach(([dc, dr]) => {
        const col = cell.col + dc;
        const row = cell.row + dr;
        const key = cellKey(col, row);
        if (present.has(key) && !visited.has(key)) {
          visited.add(key);
          queue.push({ col, row });
        }
      });
    }
    component.sort((a, b) => (a.row - b.row) || (a.col - b.col));
    components.push(component);
  });

  return components;
}

/**
 * The boundary of a component, as closed loops in grid coordinates.
 *
 * Only edges with no neighbour on the far side are emitted, so every interior cell
 * division disappears — that is what stops the merged effect showing seams where
 * two affected cells meet. A component with a hole yields more than one loop: the
 * outer contour and the hole's own, both preserved, because a ring has to keep its
 * empty middle.
 *
 * At a pinch point (two blocks meeting only at a corner) several edges leave the
 * same vertex; taking the sharpest right turn keeps the two loops separate instead
 * of stitching them into a figure of eight.
 */
export function boundaryLoops(component) {
  const present = new Set(component.map(c => cellKey(c.col, c.row)));
  const has = (col, row) => present.has(cellKey(col, row));
  const outgoing = new Map();

  const addEdge = (sx, sz, ex, ez) => {
    const key = `${sx},${sz}`;
    if (!outgoing.has(key)) outgoing.set(key, []);
    outgoing.get(key).push({ sx, sz, ex, ez, used: false });
  };

  component.forEach(({ col, row }) => {
    if (!has(col, row - 1)) addEdge(col, row, col + 1, row);
    if (!has(col + 1, row)) addEdge(col + 1, row, col + 1, row + 1);
    if (!has(col, row + 1)) addEdge(col + 1, row + 1, col, row + 1);
    if (!has(col - 1, row)) addEdge(col, row + 1, col, row);
  });

  const loops = [];
  outgoing.forEach(edges => {
    edges.forEach(start => {
      if (start.used) return;
      start.used = true;
      const loop = [[start.sx, start.sz]];
      let current = start;
      let guard = 0;
      while (guard++ < 100000) {
        loop.push([current.ex, current.ez]);
        if (current.ex === start.sx && current.ez === start.sz) break;
        const candidates = (outgoing.get(`${current.ex},${current.ez}`) || []).filter(e => !e.used);
        if (!candidates.length) break;
        let next = candidates[0];
        if (candidates.length > 1) {
          const inX = current.ex - current.sx;
          const inZ = current.ez - current.sz;
          let best = -Infinity;
          candidates.forEach(edge => {
            const outX = edge.ex - edge.sx;
            const outZ = edge.ez - edge.sz;
            const cross = inX * outZ - inZ * outX;
            const score = cross * 2 + (inX * outX + inZ * outZ);
            if (score > best) { best = score; next = edge; }
          });
        }
        next.used = true;
        current = next;
      }
      loop.pop();
      const simplified = [];
      for (let i = 0; i < loop.length; i++) {
        const a = loop[(i - 1 + loop.length) % loop.length];
        const b = loop[i];
        const c = loop[(i + 1) % loop.length];
        const turn = (b[0] - a[0]) * (c[1] - b[1]) - (b[1] - a[1]) * (c[0] - b[0]);
        if (turn !== 0) simplified.push(b);
      }
      if (simplified.length >= 4) loops.push(simplified);
    });
  });

  return loops;
}

function componentBounds(component) {
  let minCol = Infinity;
  let maxCol = -Infinity;
  let minRow = Infinity;
  let maxRow = -Infinity;
  component.forEach(({ col, row }) => {
    minCol = Math.min(minCol, col);
    maxCol = Math.max(maxCol, col + 1);
    minRow = Math.min(minRow, row);
    maxRow = Math.max(maxRow, row + 1);
  });
  return { minCol, maxCol, minRow, maxRow };
}

/**
 * A signed distance field over the component: positive inside, negative outside,
 * in cells, clamped to ±SDF_RANGE and packed into a byte per texel.
 *
 * This is what gives the effect a continuous coordinate space to live in. Edge
 * falloff, the glow at the contour, the reach of a growth front and the shape mask
 * all read from one field over the merged footprint, so nothing in the material has
 * any reason to know where the cell divisions were.
 *
 * The field is built in grid space and padded, so it survives zoom, pan and morph
 * untouched and can be cached against the cell set alone.
 */
/**
 * How far a cell's own light reaches, in cells, when the field is built radially.
 *
 * A cell is a bookkeeping square; nothing that happens on the ground is one. At 0.62
 * the disc a single cell emits covers the whole of its own edges and stops short of
 * its corners — so a lone hit reads as a disc of light rather than a lit tile — while
 * orthogonal neighbours, whose centres are one apart, overlap by a quarter of a cell
 * and fuse into one body with no seam and no step between them.
 *
 * Deliberately under 0.707. Past the half-diagonal a disc would swallow its own
 * corners and the union would go back to having them.
 */
export const CELL_RADIUS = 0.62;

/**
 * Width of the bell each cell contributes to the radial field.
 *
 * The field is a METABALL SUM, not a nearest-centre distance. Nearest-centre was the
 * obvious construction and it was wrong in a way that only shows on a real footprint:
 * twenty-five cells produced twenty-five discs with a visible dark seam wherever two
 * of them met, so a solid blast read as polka dots. Summing overlapping bells instead
 * makes neighbours FUSE — the field between two adjacent centres is higher than at
 * either of them alone — so a run of cells is one body with one boundary, and there
 * is still no corner anywhere in it.
 *
 * At 0.42 against CELL_RADIUS, the midpoint between two orthogonal neighbours sits at
 * nearly three times the surface threshold, which is well clear of any seam.
 */
const CELL_SIGMA = 0.42;

/** The level set that IS the boundary: chosen so a lone cell's disc is CELL_RADIUS. */
const CELL_LEVEL = Math.exp(-(CELL_RADIUS * CELL_RADIUS) / (2 * CELL_SIGMA * CELL_SIGMA));

/**
 * The bell's own slope where it crosses the level set. Dividing by it turns the field
 * back into something measured in cells, so everything downstream — the decal's
 * feather, the volume's clip, SDF_RANGE itself — keeps meaning what it meant when
 * this was a true distance field.
 */
const CELL_SLOPE = (CELL_RADIUS / (CELL_SIGMA * CELL_SIGMA)) * CELL_LEVEL;

export function buildSignedDistanceField(component, loops, { pixelsPerCell = 20, maxSide = 320, pad = 1.4, radial = false } = {}) {
  const bounds = componentBounds(component);
  const present = new Set(component.map(c => cellKey(c.col, c.row)));
  const rect = {
    x: bounds.minCol - pad,
    z: bounds.minRow - pad,
    width: (bounds.maxCol - bounds.minCol) + pad * 2,
    height: (bounds.maxRow - bounds.minRow) + pad * 2,
  };

  let width = Math.round(rect.width * pixelsPerCell);
  let height = Math.round(rect.height * pixelsPerCell);
  const scale = Math.min(1, maxSide / Math.max(width, height));
  width = Math.max(8, Math.round(width * scale));
  height = Math.max(8, Math.round(height * scale));

  const segments = [];
  loops.forEach(loop => {
    for (let i = 0; i < loop.length; i++) {
      const a = loop[i];
      const b = loop[(i + 1) % loop.length];
      segments.push([a[0], a[1], b[0], b[1]]);
    }
  });

  const centres = component.map(cell => [cell.col + 0.5, cell.row + 0.5]);
  const data = new Uint8Array(width * height * 4);
  for (let j = 0; j < height; j++) {
    const z = rect.z + ((j + 0.5) / height) * rect.height;
    for (let i = 0; i < width; i++) {
      const x = rect.x + ((i + 0.5) / width) * rect.width;
      let signed;
      if (radial) {
        /*
         * A UNION OF DISCS, not of squares.
         *
         * The polygon field below is exact about the cells and therefore exact about
         * their corners, and no amount of blurring downstream can take a right angle
         * out of a shape that genuinely has one — every attempt either leaves the
         * corner visible or eats so far in that a one-cell hit disappears. Measuring
         * to the cell CENTRES instead removes the corner from the geometry rather
         * than from its rendering: there is no square anywhere in the field, at any
         * resolution, at any zoom, so nothing downstream has to work to hide one.
         *
         * It still cannot overstate the claim by much. Every bell is centred on a cell
         * the effect actually hit and CELL_RADIUS is under the half-diagonal, so a
         * lone hit reaches a fifth of a cell past its own edges — the same order as
         * the falloff every other layer is already allowed — and never as far as
         * ground no cell of the union touches.
         */
        let field = 0;
        for (let c = 0; c < centres.length; c++) {
          const dx = x - centres[c][0];
          const dz = z - centres[c][1];
          const q = (dx * dx + dz * dz) / (2 * CELL_SIGMA * CELL_SIGMA);
          if (q < 14) field += Math.exp(-q);
        }
        signed = (field - CELL_LEVEL) / CELL_SLOPE;
      } else {
        const inside = present.has(cellKey(Math.floor(x), Math.floor(z)));
        let nearest = Infinity;
        for (let s = 0; s < segments.length; s++) {
          const [ax, az, bx, bz] = segments[s];
          const abx = bx - ax;
          const abz = bz - az;
          const lengthSq = abx * abx + abz * abz;
          let t = lengthSq ? ((x - ax) * abx + (z - az) * abz) / lengthSq : 0;
          t = t < 0 ? 0 : (t > 1 ? 1 : t);
          const dx = x - (ax + abx * t);
          const dz = z - (az + abz * t);
          const distanceSq = dx * dx + dz * dz;
          if (distanceSq < nearest) nearest = distanceSq;
        }
        signed = (inside ? 1 : -1) * Math.sqrt(nearest);
      }
      const level = Math.max(0, Math.min(255, Math.round(((signed / SDF_RANGE) * 0.5 + 0.5) * 255)));
      const index = (j * width + i) * 4;
      data[index] = level;
      data[index + 1] = level;
      data[index + 2] = level;
      data[index + 3] = 255;
    }
  }

  return { data, width, height, rect, bounds, radial };
}

const topologyCache = new Map();
const CACHE_LIMIT = 48;

export function topologyKey(cells, revision = 0, quality = 'high', radial = false) {
  return `${revision}|${quality}|${radial ? 'r' : 's'}|${normalizeCells(cells).map(c => cellKey(c.col, c.row)).join(';')}`;
}

/**
 * The merged topology of an authoritative cell set.
 *
 * Cached by cells alone: the same cells always produce the same components,
 * contours and field, whatever the camera is doing, so panning a board with a
 * standing effect on it costs nothing.
 */
export function buildTopology(cells, { revision = 0, quality = 'high', pixelsPerCell = 20, radial = false } = {}) {
  const key = topologyKey(cells, revision, quality, radial);
  const cached = topologyCache.get(key);
  if (cached) {
    cached.hits += 1;
    return cached.topology;
  }

  const components = connectedComponents(cells).map(component => {
    const loops = boundaryLoops(component);
    return {
      cells: component,
      keys: new Set(component.map(c => cellKey(c.col, c.row))),
      loops,
      area: component.length,
      bounds: componentBounds(component),
      sdf: buildSignedDistanceField(component, loops, { pixelsPerCell, radial }),
    };
  });

  const topology = {
    key,
    radial,
    components,
    area: components.reduce((sum, component) => sum + component.area, 0),
  };

  topologyCache.set(key, { topology, hits: 0 });
  if (topologyCache.size > CACHE_LIMIT) {
    topologyCache.delete(topologyCache.keys().next().value);
  }
  return topology;
}

export function clearTopologyCache() {
  topologyCache.clear();
}

export function topologyCacheSize() {
  return topologyCache.size;
}

/**
 * Deterministic RNG. Same seed and same cells must produce the same geometry, the
 * same particle placement and the same noise, or a preview and the execution it
 * previews would not be the same effect.
 */
export function seededRandom(seed) {
  let a = seed | 0;
  return function next() {
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
